/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/AssistBar.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)   | Who               | Why                       | What Changed          |
 * |---|---------------------|-------------------|---------------------------|------------------------|
 * | 1 | 2026-07-06          | Claude Sonnet 5   | AI Assist port (from Plex)| Created — command bar |
 * |   |                     |                   |                           | UI: ghost text, parse |
 * |   |                     |                   |                           | + resolve loop, live  |
 * |   |                     |                   |                           | autocomplete, preview |
 * |   |                     |                   |                           | chip, Pick Element    |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<RecorderPanel> renders this when canvasMode === "assist")
 *
 * EXPORTS COVERED
 * ---------------
 * - AssistBar — the ✨ Assist mode command bar + preview/confirm UI
 *
 * CALLER -> CALLEE MAP
 * ---------------------
 * - handleInputChange (debounced 250ms) -> parseAssistSentence() [Lane 0, sync]
 *                                        -> dumpTree(adb) + resolveTarget() [Lane 1, async]
 * - Confirm click / Enter key            -> buildAssistSteps() -> onAddSteps()
 * - Pick Element click                   -> onStartCapture() -> merges into buildAssistSteps()
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Ported from D:\Plex\apps\studio-web\src\components\AssistBar.tsx. One
 *    sentence = one step (or two, for "type X into Y" -> [tap, typeText]).
 *    This is NOT a chat interface — Enter/Confirm commits and clears the bar.
 * 2. Never auto-picks under ambiguity: renders all top candidates as clickable
 *    rows and requires an explicit click before Confirm is enabled.
 * 3. Unlike Plex (browser extension bridge, Selector | null capture result),
 *    Mimiq has in-process ADB access: the live tree is fetched with a direct
 *    `await dumpTree(adb)` call (no message-passing/timeout wrapper needed),
 *    and RecorderV0's setCaptureMode callback ALWAYS receives a Selector
 *    (never null) — an empty `candidates` array is how "no element found at
 *    that tap position" is signaled, per recorder.ts's #recordAndInjectTap.
 * 4. Ghost-text rotates through GRAMMAR_EXAMPLES every 3.5s while the input is
 *    empty — teaches the DSL through the UI instead of an error dialog.
 * 5. A failed parse is never a bare error: shows the 3 closest supported
 *    patterns as clickable chips that fill the input when clicked.
 * =============================================================================
 */

import { useState, useEffect, useRef, useCallback } from "react";
import type { Adb } from "@yume-chan/adb";
import type { IRStep, Selector } from "@mimiq/ir";
import { dumpTree } from "../device/treeParser.js";
import type { A11yNode } from "../device/treeParser.js";
import { flattenTree, isInteractive } from "../device/recorder.js";
import { parseAssistSentence, GRAMMAR_EXAMPLES, type AssistIntent } from "../lib/assist/grammar.js";
import { resolveTarget, accessibleName, type ResolvedMatch, type ResolveResult } from "../lib/assist/resolver.js";
import { buildAssistSteps } from "../lib/assist/buildStep.js";

type ParseState =
  | { kind: "idle" }
  | { kind: "error"; suggestions: string[] }
  | { kind: "resolving"; intent: AssistIntent }
  // `reason` distinguishes "the local ONNX model failed to load" (model-unavailable —
  // usually a one-time network fetch issue) from "genuinely no such element on
  // screen" (no-match) from "couldn't read the device tree at all"
  // (tree-unavailable) — three very different problems.
  | { kind: "no-match"; intent: AssistIntent; tree: A11yNode[] | null; reason: "tree-unavailable" | "model-unavailable" | "no-match" }
  | { kind: "ambiguous"; intent: AssistIntent; matches: ResolvedMatch[]; tree: A11yNode[] }
  | { kind: "ready"; intent: AssistIntent; resolved: ResolvedMatch | null; tree: A11yNode[] | null };

// Intents that never need element resolution — go straight to 'ready'.
const NO_RESOLUTION_KINDS = new Set<AssistIntent["kind"]>([
  "openUrl", "wait", "screenshot", "assertPageContainsText", "pressKey",
]);

type Props = {
  adb: Adb | null;
  deviceWidth?: number;
  deviceHeight?: number;
  onAddSteps: (steps: IRStep[]) => void;
  onStartCapture: (callback: (sel: Selector) => void) => void;
  onStopCapture: () => void;
};

export function AssistBar({ adb, deviceWidth, deviceHeight, onAddSteps, onStartCapture, onStopCapture }: Props) {
  const [input, setInput] = useState("");
  const [state, setState] = useState<ParseState>({ kind: "idle" });
  const [ghostIdx, setGhostIdx] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);
  const [autocomplete, setAutocomplete] = useState<string[]>([]);
  const [pickError, setPickError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);

  const screenSize = { width: deviceWidth ?? 1080, height: deviceHeight ?? 1920 };
  const disabled = !adb;

  // Rotate ghost-text examples while the bar is empty.
  useEffect(() => {
    if (input) return;
    const t = setInterval(() => setGhostIdx((i) => (i + 1) % GRAMMAR_EXAMPLES.length), 3500);
    return () => clearInterval(t);
  }, [input]);

  // Clear the success flash after a beat.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 1800);
    return () => clearTimeout(t);
  }, [flash]);

  const runParseAndResolve = useCallback(async (sentence: string) => {
    const mySeq = ++requestSeq.current;
    const trimmed = sentence.trim();
    if (!trimmed) { setState({ kind: "idle" }); setAutocomplete([]); return; }

    const result = parseAssistSentence(trimmed);
    if (!result.ok) {
      setState({ kind: "error", suggestions: result.suggestions });
      setAutocomplete([]);
      return;
    }

    const { intent } = result;

    if (NO_RESOLUTION_KINDS.has(intent.kind) || (intent.kind === "typeText" && !intent.target)) {
      setState({ kind: "ready", intent, resolved: null, tree: null });
      setAutocomplete([]);
      return;
    }

    if (!intent.target) {
      // Parsed a kind that needs a target, but none was extracted yet (still typing).
      setState({ kind: "idle" });
      return;
    }

    if (!adb) {
      setState({ kind: "no-match", intent, tree: null, reason: "tree-unavailable" });
      return;
    }

    setState({ kind: "resolving", intent });

    let tree: A11yNode[] | null;
    try {
      tree = await dumpTree(adb);
    } catch {
      tree = null;
    }
    if (mySeq !== requestSeq.current) return; // superseded by a newer keystroke

    if (!tree) {
      setState({ kind: "no-match", intent, tree: null, reason: "tree-unavailable" });
      return;
    }

    // Live autocomplete: element names from the current tree matching the role filter.
    const all = flattenTree(tree).filter(isInteractive);
    const pool = intent.role ? all.filter((n) => n.className.includes(intent.role!)) : all;
    setAutocomplete((pool.length > 0 ? pool : all).slice(0, 8).map((n) => accessibleName(n)).filter(Boolean));

    const resolveResult: ResolveResult = await resolveTarget(tree, intent.target, intent.role);
    if (mySeq !== requestSeq.current) return;

    if (resolveResult.matches.length === 0) {
      setState({ kind: "no-match", intent, tree, reason: resolveResult.reason ?? "no-match" });
    } else if (resolveResult.ambiguous) {
      setState({ kind: "ambiguous", intent, matches: resolveResult.matches, tree });
    } else {
      setState({ kind: "ready", intent, resolved: resolveResult.matches[0]!, tree });
    }
  }, [adb]);

  const handleInputChange = (value: string) => {
    setInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void runParseAndResolve(value), 250);
  };

  // Single commit path used by every state — 'ready', 'ambiguous' (after the
  // user picks a candidate), and 'no-match' (Pick Element as the sole source)
  // all funnel through here.
  const commitWith = useCallback((
    intent: AssistIntent,
    resolved: ResolvedMatch | null,
    tree: A11yNode[] | null,
    manualSelector?: Selector | null,
  ): boolean => {
    const steps = buildAssistSteps(intent, resolved, tree, screenSize, manualSelector);
    if (!steps) return false;
    onAddSteps(steps);
    setFlash(`✓ Added ${steps.length} step${steps.length > 1 ? "s" : ""}`);
    setInput("");
    setState({ kind: "idle" });
    setAutocomplete([]);
    setPickError(null);
    return true;
  }, [onAddSteps, screenSize.width, screenSize.height]);

  const commit = useCallback((manualSelector?: Selector | null) => {
    if (state.kind === "ready") commitWith(state.intent, state.resolved, state.tree, manualSelector);
  }, [state, commitWith]);

  const commitAmbiguousChoice = useCallback((match: ResolvedMatch, manualSelector?: Selector | null) => {
    if (state.kind !== "ambiguous") return;
    commitWith(state.intent, match, state.tree, manualSelector);
  }, [state, commitWith]);

  const pickElement = useCallback((onDone: (sel: Selector | null) => void) => {
    setPickError(null);
    onStartCapture((sel) => {
      // recorder.ts's setCaptureMode callback always receives a Selector —
      // an empty candidates array (not null) is how "nothing at that tap
      // position" is signaled.
      onDone(sel.candidates.length > 0 ? sel : null);
    });
  }, [onStartCapture]);

  const handlePickForReady = () => {
    pickElement((sel) => {
      if (sel) commit(sel);
      else setPickError("No element found at that position — tap directly on an element.");
    });
  };

  const handlePickForNoMatch = () => {
    if (state.kind !== "no-match") return;
    const { intent, tree } = state;
    pickElement((sel) => {
      if (sel) commitWith(intent, null, tree, sel); // manual pick is the sole candidate source
      else setPickError("No element found at that position — tap directly on an element.");
    });
  };

  const handlePickForAmbiguous = (match: ResolvedMatch) => {
    pickElement((sel) => {
      if (sel) commitAmbiguousChoice(match, sel);
      else setPickError("No element found at that position — tap directly on an element.");
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (state.kind === "ready") commit();
    } else if (e.key === "Escape") {
      onStopCapture();
      setInput("");
      setState({ kind: "idle" });
      setAutocomplete([]);
      setPickError(null);
    }
  };

  const applySuggestion = (example: string) => {
    setInput(example);
    void runParseAndResolve(example);
  };

  return (
    <div className="assist-bar">
      <div className="assist-bar-input-row">
        <span className="assist-bar-icon">✨</span>
        <input
          type="text"
          className="assist-bar-input"
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={input ? "" : GRAMMAR_EXAMPLES[ghostIdx]}
        />
        <button
          className="btn btn--primary assist-bar-add"
          onClick={() => commit()}
          disabled={state.kind !== "ready" || disabled}
        >
          ⏎ Add
        </button>
      </div>

      {disabled && (
        <div className="assist-bar-hint">Connect a device before using AI Assist.</div>
      )}

      {/* Live autocomplete hints — informational */}
      {!disabled && autocomplete.length > 0 && (state.kind === "resolving" || state.kind === "ready") && (
        <div className="assist-bar-autocomplete">
          <span className="assist-bar-autocomplete-label">on this screen:</span>
          {autocomplete.map((name, i) => (
            <span key={i} className="assist-bar-chip">{name}</span>
          ))}
        </div>
      )}

      {/* ── State-driven feedback row ── */}
      {!disabled && state.kind === "error" && (
        <div className="assist-bar-feedback">
          <div className="assist-bar-feedback-label assist-bar-feedback-label--warn">
            Not recognized yet — did you mean:
          </div>
          <div className="assist-bar-suggestions">
            {state.suggestions.map((s, i) => (
              <button key={i} className="assist-bar-chip assist-bar-chip--clickable" onClick={() => applySuggestion(s)}>{s}</button>
            ))}
          </div>
        </div>
      )}

      {!disabled && state.kind === "resolving" && (
        <div className="assist-bar-feedback assist-bar-feedback--muted">🔎 Resolving element on screen…</div>
      )}

      {!disabled && state.kind === "no-match" && (
        <div className="assist-bar-feedback">
          <div className="assist-bar-feedback-row">
            <span className="assist-bar-feedback-label assist-bar-feedback-label--warn">
              {state.reason === "tree-unavailable"
                ? "Could not read the device screen — is it still connected?"
                : state.reason === "model-unavailable"
                ? `Exact/contains match failed for "${state.intent.target}", and the local AI model couldn't load (first use needs one-time internet access to fetch it) — so no smarter match was tried.`
                : `No element found matching "${state.intent.target}".`}
            </span>
            <button className="btn btn--ghost assist-bar-pick" onClick={handlePickForNoMatch}>🎯 Pick element</button>
          </div>
          {pickError && <div className="assist-bar-pick-error">{pickError}</div>}
        </div>
      )}

      {!disabled && state.kind === "ambiguous" && (
        <div className="assist-bar-feedback">
          <div className="assist-bar-feedback-label assist-bar-feedback-label--warn">
            {state.matches.length} elements match "{state.intent.target}" — pick one:
          </div>
          {state.matches.map((m, i) => (
            <div key={i} className="assist-bar-match-row">
              <span className="assist-bar-match-class">{m.node.className.replace(/^android\.widget\./, "")}</span>
              <span className="assist-bar-match-name">{m.accessibleName}</span>
              <button className="btn btn--primary assist-bar-use" onClick={() => commitAmbiguousChoice(m)}>✓ Use this</button>
            </div>
          ))}
          <button className="btn btn--ghost assist-bar-pick" onClick={() => handlePickForAmbiguous(state.matches[0]!)}>🎯 Pick a different element</button>
          {pickError && <div className="assist-bar-pick-error">{pickError}</div>}
        </div>
      )}

      {!disabled && state.kind === "ready" && (
        <div className="assist-bar-feedback">
          <div className="assist-bar-feedback-row">
            <span className="assist-bar-preview-chip">{describeIntent(state.intent)}</span>
            <button className="btn btn--primary assist-bar-confirm" onClick={() => commit()}>✓ Confirm</button>
            {state.intent.target !== undefined && (
              <button className="btn btn--ghost assist-bar-pick" onClick={handlePickForReady}>🎯 Pick element</button>
            )}
            <button
              className="btn btn--ghost assist-bar-cancel"
              onClick={() => { setInput(""); setState({ kind: "idle" }); setPickError(null); }}
            >
              ✕
            </button>
          </div>
          {pickError && <div className="assist-bar-pick-error">{pickError}</div>}
        </div>
      )}

      {flash && <div className="assist-bar-flash">{flash}</div>}
    </div>
  );
}

function describeIntent(intent: AssistIntent): string {
  switch (intent.kind) {
    case "openUrl": return `Open ${intent.url}`;
    case "wait": return `Wait ${intent.seconds}s`;
    case "screenshot": return "Take screenshot";
    case "assertPageContainsText": return `Assert screen contains "${intent.text}"`;
    case "typeText": return intent.target ? `Type "${intent.text}" into ${intent.target}` : `Type "${intent.text}"`;
    case "tap": return `Tap ${intent.target}`;
    case "doubleTap": return `Double-tap ${intent.target}`;
    case "longPress": return `Long-press ${intent.target}`;
    case "clearText": return `Clear ${intent.target}`;
    case "waitForVisible": return `Wait for ${intent.target} to be visible`;
    case "scrollUntilVisible": return `Scroll until ${intent.target} is visible`;
    case "assertVisible": return `Assert ${intent.target} is visible`;
    case "assertNotVisible": return `Assert ${intent.target} is hidden`;
    case "assertEnabled": return `Assert ${intent.target} is enabled`;
    case "assertDisabled": return `Assert ${intent.target} is disabled`;
    case "assertHasText": return `Assert ${intent.target} has text "${intent.text}"`;
    case "pressKey": return `Press ${intent.key}`;
    default: return intent.raw;
  }
}
